import { useState, useEffect, useRef } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Clock, Send, Mail, Archive,
  MessageCircle, User, Calendar, Search,
  Inbox, MessageSquare, ArrowLeft, AlertTriangle, X, CheckCheck, Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StudentLayout, useOpenFindTeachers } from "@/components/student/layout/StudentLayout";
import { toast } from "@/hooks/use-toast";
import {
  useMyInquiries, useMessages, useSendMessage, useMarkAsRead, useCloseThread,
} from "@/hooks/use-inquiries";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useStudentProfile } from "@/contexts/StudentProfileContext";
import { OPEN_THREAD_STATUSES, type ThreadStatus } from "@/services/inquiryService";
import { cn } from "@/lib/utils";
import { EmptyState } from "@/components/common/EmptyState";
import { ReviewDialog } from "@/components/student/reviews/ReviewDialog";
import { useMyReviewFor } from "@/hooks/use-reviews";

function getInitials(name: string): string {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  if (isToday) return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return date.toLocaleDateString([], { month: "short", day: "numeric" }) +
    " · " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function daysSinceDate(dateStr: string): number {
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

// Consistent avatar gradient per teacher name
function avatarStyle(name: string) {
  const palettes = [
    { bg: "bg-indigo-500",  text: "text-white" },
    { bg: "bg-violet-500",  text: "text-white" },
    { bg: "bg-amber-500",    text: "text-white" },
    { bg: "bg-rose-500",    text: "text-white" },
    { bg: "bg-amber-500",   text: "text-white" },
    { bg: "bg-sky-500",     text: "text-white" },
    { bg: "bg-emerald-500", text: "text-white" },
    { bg: "bg-fuchsia-500", text: "text-white" },
  ];
  return palettes[name.charCodeAt(0) % palettes.length];
}

// State-ordered sort rank (lower = higher priority in list)
const STATUS_RANK: Record<ThreadStatus, number> = {
  TEACHER_REPLIED: 0,
  SENT: 1,
  STUDENT_REPLIED: 2,
  ACTIVE: 2,
  INACTIVE: 3,
  ARCHIVED: 4,
  CLOSED: 5,
};

type TabValue = "all" | "open" | "ARCHIVED";

function BrowseTeachersButton() {
  const navigate = useNavigate();
  const openFindTeachers = useOpenFindTeachers();
  return (
    <Button
      size="sm"
      variant="outline"
      className="text-xs mt-1"
      onClick={() => (openFindTeachers ? openFindTeachers() : navigate("/search"))}
    >
      Browse Teachers
    </Button>
  );
}

export default function MyInquiriesPage() {
  usePageTitle('My Inquiries | BookMyCoaching');
  const navigate = useNavigate();
  // ?thread=<id> preselects a conversation — used by the "View & reply" link in
  // inquiry emails (same param TeacherInquiriesPage accepts).
  const [searchParams] = useSearchParams();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(searchParams.get("thread"));
  const [activeTab, setActiveTab] = useState<TabValue>("all");
  const [replyText, setReplyText] = useState("");
  const [search, setSearch] = useState("");
  const [confirmClose, setConfirmClose] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { user } = useCurrentUser();
  const { profile } = useStudentProfile();

  const { data, isLoading } = useMyInquiries(0, 50);
  const { data: messagesData } = useMessages(selectedThreadId ?? undefined);
  const sendMessage = useSendMessage(selectedThreadId ?? "");
  const markAsRead = useMarkAsRead(selectedThreadId ?? "");
  const closeThread = useCloseThread(selectedThreadId ?? "");

  const allThreads = data?.content ?? [];
  const selectedThreadTeacherUserId = allThreads.find((t) => t.id === selectedThreadId)?.teacherUserId ?? "";
  const { data: myReviewData } = useMyReviewFor(selectedThreadTeacherUserId);
  const canReview = !!myReviewData?.eligible;

  const tabFiltered = allThreads.filter(t => {
    if (activeTab === "open") return OPEN_THREAD_STATUSES.has(t.status);
    if (activeTab === "ARCHIVED") return t.status === "ARCHIVED" || t.status === "CLOSED";
    return true;
  });

  const threads = search.trim()
    ? tabFiltered.filter(t =>
        (t.teacherDisplayName ?? t.teacherName).toLowerCase().includes(search.toLowerCase()) ||
        t.subject.toLowerCase().includes(search.toLowerCase())
      )
    : tabFiltered;

  const sortedThreads = [...threads].sort((a, b) => {
    const rankDiff = STATUS_RANK[a.status] - STATUS_RANK[b.status];
    if (rankDiff !== 0) return rankDiff;
    const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
    const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
    return bTime - aTime;
  });

  const selectedThread = allThreads.find((t) => t.id === selectedThreadId) ?? null;
  const messages = messagesData?.content ?? [];
  const studentName = profile?.aboutMe?.fullName || user?.name || user?.email || "";
  const totalUnread = allThreads.reduce((s, t) => s + t.unreadCount, 0);
  const openCount = allThreads.filter(t => OPEN_THREAD_STATUSES.has(t.status)).length;

  useEffect(() => {
    if (selectedThreadId) markAsRead.mutate();
  }, [selectedThreadId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (allThreads.length > 0 && !selectedThreadId) {
      setSelectedThreadId(allThreads[0].id);
    }
  }, [allThreads.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedThreadId) return;
    try {
      await sendMessage.mutateAsync(replyText.trim());
      setReplyText("");
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Failed to send";
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
  };

  const handleCloseThread = async () => {
    if (!selectedThreadId) return;
    try {
      await closeThread.mutateAsync();
      setConfirmClose(false);
      toast({ title: "Conversation closed", description: "This conversation has been closed." });
    } catch {
      toast({ title: "Error", description: "Failed to close conversation.", variant: "destructive" });
    }
  };

  const goToTeacher = (thread: typeof allThreads[number]) => {
    const handle = thread.teacherHandle;
    if (handle) navigate(`/t/${handle}`);
  };

  const tabs: { value: TabValue; label: string; count?: number }[] = [
    { value: "all", label: "All", count: allThreads.length },
    { value: "open", label: "Open", count: openCount },
    { value: "ARCHIVED", label: "Archived" },
  ];

  const [mobileShowConversation, setMobileShowConversation] = useState(searchParams.has("thread"));

  const handleSelectThread = (id: string) => {
    setSelectedThreadId(id);
    setConfirmClose(false);
    setMobileShowConversation(true);
  };

  const isThreadOpen = selectedThread ? OPEN_THREAD_STATUSES.has(selectedThread.status) : false;

  return (
    <StudentLayout contentClassName="overflow-hidden" contentStyle={{ height: "calc(100svh - 3.5rem)" }}>

      {/* ── Page header ── */}
      <div className={cn(
        "px-4 sm:px-5 pt-4 pb-3 border-b border-slate-100 shrink-0 flex items-center justify-between gap-4",
        mobileShowConversation && "hidden md:flex"
      )}>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-slate-900">Inquiries</h1>
            {totalUnread > 0 && (
              <span className="inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-full bg-indigo-600 text-white">
                {totalUnread} new
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-0.5">Your conversations with teachers</p>
        </div>
        <div className="flex items-center gap-3 text-right">
          <div>
            <p className="text-lg font-bold text-slate-800 leading-none">{openCount}</p>
            <p className="text-xs text-slate-400 mt-0.5">open</p>
          </div>
          <div className="w-px h-6 bg-slate-100" />
          <div>
            <p className="text-lg font-bold text-slate-800 leading-none">{allThreads.length}</p>
            <p className="text-xs text-slate-400 mt-0.5">total</p>
          </div>
        </div>
      </div>

      {/* ── Split panel ── */}
      <div className="flex-1 flex overflow-hidden">

        {/* ── Left: thread list ── */}
        <div className={cn(
          "flex flex-col border-r border-slate-100 bg-white overflow-hidden",
          "w-full md:w-72 lg:w-80 md:shrink-0",
          mobileShowConversation ? "hidden md:flex" : "flex"
        )}>
          {/* Search + tabs */}
          <div className="p-3 border-b border-slate-100 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 placeholder:text-slate-400 transition-all"
              />
            </div>
            <div className="flex gap-0.5">
              {tabs.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => { setActiveTab(tab.value); setSelectedThreadId(null); setMobileShowConversation(false); }}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold rounded-lg transition-all",
                    activeTab === tab.value
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                  )}
                >
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className={cn(
                      "text-xs font-bold px-1.5 py-0.5 rounded-full leading-none",
                      activeTab === tab.value ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
                    )}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Thread list */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              [1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3.5 animate-pulse border-b border-slate-50">
                  <div className="w-10 h-10 rounded-full bg-slate-100 shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 bg-slate-100 rounded-full w-1/2" />
                    <div className="h-3 bg-slate-100 rounded-full w-3/4" />
                  </div>
                </div>
              ))
            ) : threads.length === 0 ? (
              search ? (
                <EmptyState
                  compact
                  icon={Search}
                  title="No results"
                  description="Try a different search term"
                  iconBgClassName="bg-slate-50"
                  iconClassName="text-slate-400"
                />
              ) : (
                <EmptyState
                  compact
                  icon={Inbox}
                  title="No inquiries yet"
                  description="Start by finding a teacher"
                  iconBgClassName="bg-slate-50"
                  iconClassName="text-slate-400"
                  action={<BrowseTeachersButton />}
                />
              )
            ) : (
              sortedThreads.map((thread) => {
                const isUnread = thread.unreadCount > 0;
                const isSelected = thread.id === selectedThreadId;
                const isInactive = thread.status === "INACTIVE";
                const isClosed = thread.status === "CLOSED";
                const isArchived = thread.status === "ARCHIVED" || isClosed;
                const style = avatarStyle(thread.teacherDisplayName ?? thread.teacherName ?? "T");
                const daysSinceMsg = thread.lastMessageAt ? daysSinceDate(thread.lastMessageAt) : 0;
                return (
                  <button
                    key={thread.id}
                    onClick={() => handleSelectThread(thread.id)}
                    className={cn(
                      "w-full flex items-start gap-3 px-4 py-3.5 text-left transition-all border-b border-slate-50",
                      isSelected ? "bg-indigo-50" : "hover:bg-slate-50/80",
                    )}
                  >
                    {/* Avatar */}
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-sm font-bold mt-0.5",
                      style.bg, style.text
                    )}>
                      {getInitials(thread.teacherDisplayName ?? thread.teacherName ?? "T")}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className={cn(
                          "text-sm truncate",
                          isUnread ? "font-bold text-slate-900" : "font-medium text-slate-700"
                        )}>
                          {thread.teacherDisplayName ?? thread.teacherName}
                        </span>
                        <span className="text-xs text-slate-400 shrink-0">
                          {thread.lastMessageAt ? formatTimeAgo(thread.lastMessageAt) : formatTimeAgo(thread.updatedAt)}
                        </span>
                      </div>
                      <p className={cn(
                        "text-xs truncate leading-relaxed",
                        isUnread ? "text-slate-600 font-medium" : "text-slate-400"
                      )}>
                        {thread.lastMessage?.content || thread.subject}
                      </p>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        {/* Status pill */}
                        <span className={cn(
                          "text-xs font-semibold px-2 py-0.5 rounded-full",
                          isClosed   ? "bg-slate-100 text-slate-400" :
                          isArchived ? "bg-slate-100 text-slate-500" :
                          isInactive ? "bg-amber-100 text-amber-700" :
                                       "bg-emerald-100 text-emerald-700"
                        )}>
                          {isClosed ? "Closed" : isArchived ? "Archived" : isInactive ? "Inactive" : "Open"}
                        </span>
                        {isUnread && (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-indigo-600 text-white">
                            {thread.unreadCount} new
                          </span>
                        )}
                        {isInactive && !isUnread && (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 inline-flex items-center gap-0.5">
                            <AlertTriangle className="w-2.5 h-2.5" /> {daysSinceMsg}d
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ── Right: conversation ── */}
        <div className={cn(
          "flex-1 flex flex-col min-w-0 bg-white overflow-hidden",
          mobileShowConversation ? "flex" : "hidden md:flex"
        )}>
          {!selectedThread ? (
            <div className="flex-1 flex items-center justify-center">
              <EmptyState
                icon={MessageCircle}
                title="No conversation selected"
                description="Pick an inquiry from the list to start reading"
                iconBgClassName="bg-indigo-50"
                iconClassName="text-indigo-400"
              />
            </div>
          ) : (
            <div className="flex-1 flex flex-col min-h-0">

              {/* Conversation header */}
              <div className="border-b border-slate-100 shrink-0 px-4 sm:px-5 py-3.5 bg-white">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Mobile back */}
                    <button
                      className="md:hidden p-1.5 -ml-1 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
                      onClick={() => setMobileShowConversation(false)}
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </button>
                    {/* Avatar */}
                    {(() => {
                      const style = avatarStyle(selectedThread.teacherDisplayName ?? selectedThread.teacherName ?? "T");
                      return (
                        <div className={cn(
                          "w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-bold",
                          style.bg, style.text
                        )}>
                          {getInitials(selectedThread.teacherDisplayName ?? selectedThread.teacherName ?? "T")}
                        </div>
                      );
                    })()}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold text-slate-800 truncate">
                          {selectedThread.teacherDisplayName ?? selectedThread.teacherName}
                        </p>
                        {/* Status badge */}
                        {selectedThread.status === "CLOSED" ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 shrink-0">
                            <X className="w-2.5 h-2.5" /> Closed
                          </span>
                        ) : selectedThread.status === "ARCHIVED" ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 shrink-0">
                            <Archive className="w-2.5 h-2.5" /> Archived
                          </span>
                        ) : selectedThread.status === "INACTIVE" ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 shrink-0">
                            <AlertTriangle className="w-2.5 h-2.5" /> Inactive
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Open
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 truncate">{selectedThread.subject}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {canReview && selectedThread.teacherUserId && (
                      <ReviewDialog
                        teacherUserId={selectedThread.teacherUserId}
                        teacherName={selectedThread.teacherDisplayName ?? selectedThread.teacherName}
                      />
                    )}
                    {isThreadOpen && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-3 text-xs text-slate-400 hover:text-red-500 hover:bg-red-50"
                        onClick={() => setConfirmClose(true)}
                      >
                        Close
                      </Button>
                    )}
                  </div>
                </div>

                {/* Shared email / time slots */}
                {(selectedThread.sharedEmail || (selectedThread.preferredTimeSlots?.length ?? 0) > 0) && (
                  <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex flex-wrap gap-3">
                    {selectedThread.sharedEmail && (
                      <button
                        type="button"
                        onClick={() => goToTeacher(selectedThread)}
                        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-indigo-600 transition-colors"
                      >
                        <Mail className="w-3.5 h-3.5 text-indigo-400" />
                        <span className="font-medium hover:underline underline-offset-2">{selectedThread.sharedEmail}</span>
                      </button>
                    )}
                    {(selectedThread.preferredTimeSlots?.length ?? 0) > 0 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <Calendar className="w-3.5 h-3.5" /> Availability:
                        </span>
                        {selectedThread.preferredTimeSlots!.map((s, i) => (
                          <span key={i} className="text-xs font-medium px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-100">
                            {s.date} · {s.timeSlot}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Confirm close */}
              {confirmClose && (
                <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-100 rounded-xl flex items-center justify-between gap-3 shrink-0">
                  <p className="text-xs text-red-700 font-medium">Close this conversation? This cannot be undone.</p>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setConfirmClose(false)}>Cancel</Button>
                    <Button size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={handleCloseThread} disabled={closeThread.isPending}>
                      {closeThread.isPending ? "Closing…" : "Close"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-3" style={{ background: "#f8f9fc" }}>
                {messages.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full gap-2 py-16 text-center">
                    <div className="w-12 h-12 rounded-2xl bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                      <MessageSquare className="w-5 h-5 text-slate-300" />
                    </div>
                    <p className="text-sm text-slate-400">No messages yet — say hello!</p>
                  </div>
                )}
                {messages.map((msg) => {
                  const isMine = msg.senderUserId === user?.id;
                  const teacherStyle = avatarStyle(selectedThread.teacherDisplayName ?? selectedThread.teacherName ?? "T");
                  return (
                    <div key={msg.id} className={cn("flex gap-2", isMine ? "flex-row-reverse" : "flex-row")}>
                      {/* Avatar */}
                      <div className={cn(
                        "w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold self-end mb-4",
                        isMine ? "bg-indigo-600 text-white" : `${teacherStyle.bg} ${teacherStyle.text}`
                      )}>
                        {isMine ? getInitials(studentName || "S") : getInitials(selectedThread.teacherDisplayName ?? selectedThread.teacherName ?? "T")}
                      </div>
                      <div className={cn("max-w-[70%] flex flex-col gap-1", isMine ? "items-end" : "items-start")}>
                        <div className={cn(
                          "px-4 py-2.5 text-sm leading-relaxed",
                          isMine
                            ? "bg-indigo-600 text-white rounded-2xl rounded-br-md"
                            : "bg-white border border-slate-200 text-slate-700 rounded-2xl rounded-bl-md shadow-sm"
                        )}>
                          {msg.content}
                        </div>
                        <div className={cn("flex items-center gap-1 px-1", isMine ? "flex-row-reverse" : "flex-row")}>
                          <p className="text-xs text-slate-400">{formatTime(msg.createdAt)}</p>
                          {isMine && (
                            msg.seenAt
                              ? <CheckCheck className="w-3 h-3 text-indigo-400" />
                              : <Check className="w-3 h-3 text-slate-300" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply bar */}
              {selectedThread.status === "CLOSED" ? (
                <div className="mx-4 mb-4 py-3 px-4 bg-slate-50 rounded-xl border border-slate-200 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                  <X className="w-3.5 h-3.5" /> This conversation was closed.
                </div>
              ) : selectedThread.status === "ARCHIVED" ? (
                <div className="mx-4 mb-4 py-3 px-4 bg-slate-50 rounded-xl border border-slate-200 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                  <Archive className="w-3.5 h-3.5" /> This conversation is archived.
                </div>
              ) : (
                <div className="px-4 pb-4 pt-2 shrink-0">
                  <div className="flex items-end gap-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-3">
                    <Textarea
                      placeholder="Write a message…"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows={1}
                      className="flex-1 resize-none border-0 bg-transparent shadow-none focus-visible:ring-0 p-0 text-sm placeholder:text-slate-300 min-h-[36px] max-h-32"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendReply(); }
                      }}
                    />
                    <Button
                      onClick={handleSendReply}
                      disabled={!replyText.trim() || sendMessage.isPending}
                      size="sm"
                      className="shrink-0 h-9 w-9 p-0 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 transition-all"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 text-center mt-1.5">Press Enter to send · Shift+Enter for new line</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </StudentLayout>
  );
}
